<template>
    <v-layout row wrap>
        <v-flex xs10 offset-xs1 lg8 offset-lg2>
            <v-form ref="form" @submit.prevent="submit">
                <v-card class="pa-3" v-if="!loading">
                    <v-card-text>

                        <div class="subheading">{{ $t('label.basicInfo') }}</div>
                        <v-layout row v-if="isAdmin">
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.requestPersonUpdate') }}?</label>
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-checkbox v-model="form.isUpdateRequired" color="primary"></v-checkbox>
                            </v-flex>
                        </v-layout>
                        <p v-if="isFirstLogin">{{ $t('message.info.personFirstLogin') }}.</p>
                        <p v-if="isUpdateRequired">{{ $t('message.info.personUpdateRequired') }}.</p>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.name') }}</label>
                            </v-flex>
                            <v-flex xs7 lg5>
                                <span class="form-value">{{ name }}</span>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.user') }}</label>
                            </v-flex>
                            <v-flex xs7 lg5>
                                <span class="form-value">{{ user }}</span>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.email') }}*</label>
                            </v-flex>
                            <v-flex xs7 lg7>
                                <v-text-field :disabled="disabled" v-model="form.emailAddress" :rules="validation.requiredEmail" :validate-on-blur="true" :counter="constants.LENGTH_EMAIL" :maxlength="constants.LENGTH_EMAIL"></v-text-field>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.hired') }}?</label>
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-checkbox :disabled="!isRoot" v-model="form.isHired" color="primary"></v-checkbox>
                            </v-flex>
                        </v-layout>

                        <v-layout row v-if="isAdmin">
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.isAdmin') }}?</label>
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-checkbox :disabled="disabled || !canSetAdmin" v-model="form.isAdmin" color="primary"></v-checkbox>
                            </v-flex>
                        </v-layout>

                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.isOutsourced') }}?</label>
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-checkbox :disabled="disabled || (!isAdmin && !isFirstLogin) || form.isHired" v-model="form.isOutsourced" color="primary" :rules="outsourcedAdminRule"></v-checkbox>
                            </v-flex>
                        </v-layout>

                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.active') }}?</label>
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-checkbox :disabled="disabled || !canSetAdmin" v-model="form.isActive" color="primary"></v-checkbox>
                            </v-flex>
                        </v-layout>

                        <v-layout row v-if="!form.isHired">
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.office') }}*</label>
                            </v-flex>
                            <v-flex xs7 lg6>
                                <v-select autocomplete @change="onChangeSecArea" :disabled="disabled || outsourced || form.isHired" v-bind:items="offices" v-model="form.office.id" :rules="validation.required" :no-data-text="$t('message.warning.noData')"></v-select>
                            </v-flex>
                        </v-layout>
                        <v-layout row v-if="!form.isHired">
                            <v-flex xs3 lg2>
                                <label class="form-label">{{ $t('label.area') }}*</label>
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select autocomplete @change="onChangeSecArea" :disabled="disabled || outsourced || form.isHired" v-bind:items="areas" v-model="form.area.id" :rules="validation.required" :no-data-text="$t('message.warning.noData')">
                                </v-select>
                            </v-flex>
                        </v-layout>

                        <div v-if="!isFirstLogin && !isSubstitute">
                            <div class="subheading mt-3">{{ $t('label.substitute') }}</div>
                            <p v-html="$t('message.info.substitute')"></p>
                            <v-layout row>
                                <v-flex xs3 lg2>
                                    <label class="form-label">{{ $t('label.person') }}</label>
                                </v-flex>
                                <v-flex xs9 lg10>
                                    <v-select autocomplete :disabled="disabled" v-bind:items="persons" v-model="form.substitute.id" :no-data-text="$t('message.warning.noData')"></v-select>
                                </v-flex>
                            </v-layout>
                            <v-layout row v-if="hasSubstitute">
                                <v-flex xs3 lg2>
                                    <label class="form-label">{{ $t('label.startDate') }}</label>
                                </v-flex>
                                <v-flex xs2 lg2>
                                    <app-menu-date-picker :disabled="disabled" :model.sync="form.substitutionTerm.start" :rules="validation.required"></app-menu-date-picker>
                                </v-flex>
                            </v-layout>
                            <v-layout row v-if="hasSubstitute">
                                <v-flex xs3 lg2>
                                    <label class="form-label">{{ $t('label.endDate') }}</label>
                                </v-flex>
                                <v-flex xs2 lg2>
                                    <app-menu-date-picker :disabled="disabled" :model.sync="form.substitutionTerm.finish" :rules="substitutionTermFinishRule"></app-menu-date-picker>
                                </v-flex>
                            </v-layout>
                        </div>

                        <div v-if="!isFirstLogin && isSubstitute">
                            <div class="subheading mt-3">{{ $t('label.substitutes') }}</div>
                            <p v-html="$t('message.info.substitutes')"></p>
                            <v-layout row v-for="person in form.substitutes" :key="person.id" class="row--short">
                                <v-flex xs3 lg2>
                                    <label class="form-label">{{ person.username }}</label>
                                </v-flex>
                                <v-flex xs9 lg10>
                                    <span class="form-value">{{ person.name }}</span>
                                </v-flex>
                            </v-layout>
                        </div>
                    </v-card-text>

                    <v-card-actions v-if="!disabled">
                        <v-btn :loading="saving" @click="save" color="primary">{{ $t('label.save') }}</v-btn>
                        <v-btn :disabled="saving" @click="switchUser" color="primary" v-if="isFirstLogin || isUpdateRequired">{{ $t('label.switchUser') }}
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn :disabled="saving" @click="clearSubstitute" v-if="!isFirstLogin && !isSubstitute" color="secondary">{{ $t('label.clearSubstitute') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
                <v-card v-else>
                    <app-card-loader></app-card-loader>
                </v-card>
            </v-form>
        </v-flex>
    </v-layout>
</template>

<script>
import constants from '@/helpers/constants';
import utilities from '@/helpers/utilities';
import validation from '@/helpers/validation';
import Person from '@/helpers/entities/Person';

import CardLoader from '@/components/shared/CardLoader';
import MenuDatePicker from '@/components/shared/MenuDatePicker';

export default {
    data() {
        return {
            constants,
            validation,
            valid: true,
            saving: false,
            loading: true,

            offices: this.$store.getters.officesValueText,
            //  areas: this.$store.getters.areasValueText,

            profile: this.$store.getters.profile,
            isFirstLogin: this.$store.getters.isFirstLogin,
            isUpdateRequired: this.$store.getters.isPersonUpdateRequired,

            form: Person.build(),
            substitutionTermFinishRule: [
                ...validation.required,
                () => this.form.substitutionTerm.finish >= this.form.substitutionTerm.start || this.$t('message.error.period'),
            ],
            outsourcedAdminRule: [
                () => !(this.form.isOutsourced && this.form.isAdmin) || this.$t('message.error.outsourcedAdmin'),
            ],
        };
    },
    computed: {
        isAdmin() {
            const ok = this.$store.getters.isAdmin || this.$store.getters.profile.isRoot;
            return ok;
        },
        isRoot() {
            const ok = this.$store.getters.profile.isRoot;
            return ok;
        },
        canSetAdmin() {
            if (utilities.isEmptyOrZero(this.form.id)) return false;
            const ok = (this.isAdmin &&
                this.$store.getters.profile.person.id !== this.form.id &&
                this.$store.getters.profile.office === this.form.office.id) ||
                this.isRoot;
            return ok;
        },
        areas() {
            const office = this.form.office.id;
            let temp = this.$store.getters.areas.filter(v => v.office.id === office
                && v.isActive);

            if (this.form.isOutsourced) {
                temp = temp.filter(v => v.isOutsourced === this.form.isOutsourced);
            } else {
                temp = temp.filter(v => !v.isOutsourced);
            }

            return temp.map(({ id, acronym, name }) => ({ value: id, text: `${acronym} - ${name}` }));
        },
        disabled() {
            if (utilities.isEmptyOrZero(this.form.id)) return false;

            if (this.$store.getters.isAdmin || this.$store.getters.profile.isRoot) return false;

            return this.profile.person.id !== this.form.id;
        },
        hasSubstitute() {
            return !utilities.isEmptyOrZero(this.form.substitute.id);
        },
        isSubstitute() {
            return !utilities.isEmptyOrZero(this.form.substitutes);
        },
        name() {
            if (this.isFirstLogin) return this.profile.name;

            return this.form.name;
        },
        user() {
            if (this.isFirstLogin) return this.profile.login;

            return this.form.username;
        },
        persons() {
            let persons = [];
            if (this.form.isHired) {
                this.form.office.id = 0;
                this.form.area.id = 0;
                persons = this.$store.getters.personsHired;
            } else {
                persons = this.$store.getters.personsNotHired;
            }

            if (this.form.office.id > 0) {
                persons = persons.filter(person => person.office
                                            && person.office.id === this.form.office.id);
            }

            if (this.form.isOutsourced) {
                persons = persons.filter(person => person.area
                                            && person.area.id === this.form.area.id);
            }
            persons = persons
                    .filter(person => person.isActive)
                    .map(({ id, username, name }) => ({
                        value: id,
                        text: `${username} - ${name}`
                    }));
            return persons.filter(i => this.form.id !== i.value);
        },
        outsourced() {
            // if (this.isFirstLogin && !utilities.isEmptyOrZero(this.profile.area.id)) return true;

            return false;
        },
    },

    components: {
        appCardLoader: CardLoader,
        appMenuDatePicker: MenuDatePicker,
    },

    watch: {
        $route: 'fetch',
    },

    created() {
        this.fetch();
    },

    methods: {
        clearSubstitute() {
            this.form.substitute = { id: null };
            this.form.substitutionTerm.start = null;
            this.form.substitutionTerm.finish = null;
        },
        onChangeSecArea() {
            this.clearSubstitute();
        },
        async fetch() {
            this.loading = true;

            if (!utilities.isEmptyOrZero(this.$route.params.id)) {
                await this.$store.dispatch('fetchPerson', this.$route.params.id);
                this.form = Person.toForm(this.$store.getters.person);
            }

            this.loading = false;
        },

        async save() {
            if (!this.$refs.form.validate()) return;
            try {
                this.saving = true;

                this.form.isUpdateRequired = false;

                await this.$store.dispatch('saveOrUpdatePerson', Person.toPayload(this.form));

                this.$router.push({ name: 'personIndex' });

            } catch (error) {
                if (error.data && (error.data.code === "generic_error")) {
                    this.saving = false;
                } else {
                    throw error;
                }
            }

        },
        switchUser() {
            this.$router.push({ name: 'login' });
        },
    },
};
</script>




// WEBPACK FOOTER //
// src/components/persons/Edit.vue